/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

(() => {
  class MorganTaylorColorBrowser extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.range = this.querySelector('[data-color-range]');
      this.label = this.querySelector('[data-color-label]');
      this.buttons = [...this.querySelectorAll('[data-color-index]')];
      this.groups = [...this.querySelectorAll('[data-color-group]')];

      this.range?.addEventListener('input', () => this.show(Number(this.range.value)), { signal });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-color-index]');
        if (button) this.show(Number(button.dataset.colorIndex));
      }, { signal });
      this.show(Number(this.range?.value) || 0);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    show(index) {
      if (!this.groups.length) return;
      const nextIndex = Math.min(this.groups.length - 1, Math.max(0, index));
      this.groups.forEach((group, groupIndex) => {
        const active = groupIndex === nextIndex;
        group.hidden = !active;
        group.inert = !active;
      });
      this.buttons.forEach((button, buttonIndex) => {
        button.setAttribute('aria-selected', String(buttonIndex === nextIndex));
      });
      if (this.range) this.range.value = String(nextIndex);
      if (this.label) this.label.textContent = this.buttons[nextIndex]?.textContent?.trim() || '';
    }
  }

  class MorganTaylorStepTabs extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.buttons = [...this.querySelectorAll('[data-morgan-step-index]')];
      this.groups = [...this.querySelectorAll('[data-morgan-step-group]')];

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-morgan-step-index]');
        if (button) this.show(Number(button.dataset.morganStepIndex));
      }, { signal });
      this.addEventListener('keydown', (event) => {
        const button = event.target.closest('[data-morgan-step-index]');
        if (!button || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const current = this.buttons.indexOf(button);
        let next = current;
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.buttons.length - 1;
        else if (event.key === 'ArrowRight') next = (current + 1) % this.buttons.length;
        else next = (current - 1 + this.buttons.length) % this.buttons.length;
        this.show(next, true);
      }, { signal });
      this.show(0);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    show(index, focus = false) {
      if (!this.groups.length) return;
      const nextIndex = Math.min(this.groups.length - 1, Math.max(0, index));
      this.groups.forEach((group, groupIndex) => {
        const active = groupIndex === nextIndex;
        group.hidden = !active;
        group.inert = !active;
        group.classList.toggle('is-active', active);
        if (active) group.querySelector('product-instructions')?.show?.(0);
      });
      this.buttons.forEach((button, buttonIndex) => {
        const active = buttonIndex === nextIndex;
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
        if (active && focus) button.focus();
      });
    }
  }

  if (!customElements.get('morgan-taylor-color-browser')) {
    customElements.define('morgan-taylor-color-browser', MorganTaylorColorBrowser);
  }
  if (!customElements.get('morgan-taylor-step-tabs')) {
    customElements.define('morgan-taylor-step-tabs', MorganTaylorStepTabs);
  }
})();
